import React, { Fragment, Component } from 'react';
import { Helmet } from 'react-helmet';
import { Box } from 'localmed-core';

import { createSchedulingSessionStateLandingPage } from '../../../shared/components/SchedulingSessionState';
import { trackAffiliateEvent } from '../../../shared/utils/affiliateTracking';
import getSchedulingParams from '../../../shared/utils/getSchedulingParams';
import WidgetLayout from '../../components/WidgetLayout';
import WidgetProviderHeader from '../../components/WidgetProviderHeader';
import LoadingPage from '../LoadingPage';
import NotFoundPage from '../NotFoundPage';
import WidgetForm from '../../components/WidgetForm';
import WidgetRatings from '../../components/WidgetRatings';
import ForceSelection from '../../components/ForceSelection';
import ProviderBodyWithData from './ProviderBodyWithData';
import { WIDGET_CHROME, PATIENT_STATUS } from '../../../shared/constants';

import { withCustomContexts } from '../../../shared/components/Contexts';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import PatientScreener, {
  PatientScreenerFailure,
  PatientScreenerSuccess,
  SCREENER_STATUSES,
} from '../../../shared/components/PatientScreener';
import ProviderPageFlashMessage from './ProviderPageFlashMessage';

class ProviderSchedulingPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showScreenerSuccess: false,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      data: { provider },
      patientScreenerStatus,
    } = this.props;
    const office = provider && provider.office;
    if (
      !office ||
      !office.patientScreenerEnabled ||
      prevProps.patientScreenerStatus === patientScreenerStatus
    )
      return;
    if (patientScreenerStatus === SCREENER_STATUSES.PASSED) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showScreenerSuccess: true });
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const {
      data: { loading, provider },
      match: { params },
    } = nextProps;
    const { tracked } = this;
    const {
      widgetSettings,
      universalContext: { trackEvent },
    } = this.props;

    if (tracked || loading) return;

    if (provider) {
      trackEvent({
        event: 'Viewed Provider',
        properties: {
          widget: true,
          providerId: provider.id,
          providerDisplay: provider.display,
        },
      });
    } else {
      trackEvent({
        event: 'Provider Widget Not Found',
        properties: { providerId: params.id },
      });
    }

    trackEvent({ event: 'Viewed Widget Version 2' });
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Widget',
    });

    this.tracked = true;
  }

  tracked = false;

  closeScreenerSuccess = () => {
    this.setState({ showScreenerSuccess: false });
  };

  renderOnlineSchedulingUnavailable(provider) {
    return (
      <div className="padding-sm text-align-center">
        <h2 className="font-family-meta-bold font-size-lg line-height-solid margin-top-lg margin-bottom-xs">
          {provider.display} is not accepting appointments online at the moment.
        </h2>
        <p>
          Please call the office at{' '}
          <span className="text-nowrap font-family-meta-bold">{provider.office.phone}</span> to
          schedule your appointment.
        </p>
      </div>
    );
  }

  renderWidgetBody(schedulingParams) {
    const { data } = this.props;
    const { provider } = data;
    const { office } = provider;
    const {
      updatePatientStatus,
      updateReasonForVisit,
      values: { patientStatus, reasonForVisit, reasonsForVisit },
    } = schedulingParams;

    // Update Display message based on patient status and available reasons for that status
    const patientStatusItems = [
      { id: PATIENT_STATUS.NEW, display: 'New Patient' },
      { id: PATIENT_STATUS.RETURNING, display: 'Returning Patient' },
    ];
    const selectedPatientStatus =
      patientStatusItems.find(p => p.id === patientStatus) || patientStatusItems[0];
    const reasonForVisitItems = reasonsForVisit.filter(
      r => r.patientStatus === selectedPatientStatus.id
    );
    const noOnlineSchedulingForStatus = reasonForVisitItems.length === 0;

    return (
      <Fragment>
        <WidgetForm
          reasonForVisit={reasonForVisit}
          reasonForVisitItems={reasonForVisitItems}
          selectedPatientStatus={selectedPatientStatus}
          noOnlineSchedulingForStatus={noOnlineSchedulingForStatus}
          onChangePatientStatus={updatePatientStatus}
          onChangeReasonForVisit={updateReasonForVisit}
        />
        {noOnlineSchedulingForStatus ? (
          <div className="padding-sm text-align-center" key="noOnlineSchedulingForStatus">
            <h2 className="font-family-meta-bold font-size-lg line-height-solid margin-top-lg margin-bottom-xs">
              {provider.display} is not accepting {selectedPatientStatus.id.toLowerCase()} patients
              online at the moment.
            </h2>
            <p>
              Please call us at{' '}
              <span className="text-nowrap font-family-meta-bold">{provider.office.phone}</span> to
              schedule your appointment.
            </p>
          </div>
        ) : (
          <ForceSelection selected={reasonForVisit != null}>
            {reasonForVisit && (
              <ProviderBodyWithData
                reasonForVisit={reasonForVisit}
                schedule={schedulingParams}
                provider={provider}
                isOnWidget
                showFlashMessage={!office.patientScreenerEnabled}
              />
            )}
          </ForceSelection>
        )}
      </Fragment>
    );
  }

  render() {
    const {
      widgetSettings: { chrome },
      data: { provider, loading },
      history,
      patientScreenerStatus,
    } = this.props;

    if (loading) return <LoadingPage />;
    if (!provider || provider.archivedAt) return <NotFoundPage />;

    const schedulingParams = getSchedulingParams({
      history,
      reasonsForVisit: provider.reasonsForVisit.nodes,
    });

    const { reasonsForVisit } = schedulingParams.values;
    const { office } = provider;
    const { announcementEnabled, announcement } = office;
    const noOnlineScheduling = reasonsForVisit.length === 0 || !provider.onlineSchedulingEnabled;
    const showScheduling =
      patientScreenerStatus === SCREENER_STATUSES.PASSED || !office.patientScreenerEnabled;

    return (
      <WidgetLayout
        header={<WidgetProviderHeader provider={provider} />}
        isEmbedded={chrome === WIDGET_CHROME.EMBEDDED}
      >
        <Helmet>
          <title>{provider.display}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        {announcementEnabled && <AnnouncementBanner announcement={announcement} />}
        {showScheduling ? (
          <Fragment>
            {this.state.showScreenerSuccess && (
              <PatientScreenerSuccess onClose={this.closeScreenerSuccess} />
            )}
            {office.reviewsEnabled && (
              <WidgetRatings
                ratings={[
                  office.localmedRating,
                  office.googleRating,
                  office.facebookRating,
                  office.yelpRating,
                ]}
              />
            )}

            {noOnlineScheduling
              ? this.renderOnlineSchedulingUnavailable(provider)
              : this.renderWidgetBody(schedulingParams)}
          </Fragment>
        ) : (
          <Box maxWidth="720px" m="0 auto">
            <ProviderPageFlashMessage location={history.location} m="md" marginBottom="none" />
            {patientScreenerStatus === SCREENER_STATUSES.NOT_SCREENED && (
              <PatientScreener
                officeId={office.id}
                officeDisplay={office.display}
                providerId={provider.id}
                providerDisplay={provider.display}
                widget
              />
            )}
            {patientScreenerStatus === SCREENER_STATUSES.FAILED && (
              <PatientScreenerFailure phoneNumber={office.phone} />
            )}
          </Box>
        )}
      </WidgetLayout>
    );
  }
}

export default createSchedulingSessionStateLandingPage(withCustomContexts(ProviderSchedulingPage));
